// The registry of settings bodies: what each section address shows when its
// page opens. This is the one place an address meets its component. The
// shell (SettingsGroup.jsx) draws the page's header and tabs around it, so a
// body draws no title of its own; names, copy and gates are
// lib/settingsModel.js's. Dashboard chat's sections open in
// ChatSettingsDialog instead. `sections` is every section a merged tab or
// page holds, for a body that draws more than its own (Set up's check-ins).
import BrowserCaptureSettings from "../BrowserCaptureSettings.jsx";
import OtelMirrorSettings from "../OtelMirrorSettings.jsx";
import IntegrationsTab from "../identity/IntegrationsTab.jsx";
import DirectoryBrowserTab from "../identity/DirectoryBrowserTab.jsx";
import ConnectPage, { EnrolledEndpoints } from "../../pages/ConnectPage.jsx";
import ProvisioningTokensPage from "../../pages/ProvisioningTokensPage.jsx";
import InstallersPage from "../../pages/InstallersPage.jsx";
import ArtifactRolloutsPage from "../../pages/ArtifactRolloutsPage.jsx";
import UnassignedEndpointsPage from "../../pages/UnassignedEndpointsPage.jsx";
import UserManagementPage from "../../pages/UserManagementPage.jsx";
import EscalationsPage from "../../pages/EscalationsPage.jsx";
import DataSourcesPage from "../../pages/DataSourcesPage.jsx";
import ReportPage from "../../pages/ReportPage.jsx";
import NotificationsPage from "../../pages/NotificationsPage.jsx";
import { EndpointConfigurationSection } from "../../pages/SettingsPage.jsx";
import { ReviewPolicy, ReviewProfileItems, ReviewStandards } from "./TraceReviewSettings.jsx";
import { AccountNotifications, AccountProfile } from "./YourAccount.jsx";
import { ScrollRoot } from "../ui/scrollRoot.js";

// People, Groups and Devices are one browser in three tabs, laid out to fill
// the screen: it gets the height left under the page's header, scrolls its
// own lists rather than the page, and the page's side inset is zeroed inside it. Each tab renders it in the same place, so a
// person's group opened from their detail stays selected as the tab changes.
function Browse({ section, onSectionChange }) {
    return (
        <div className="flex h-[max(520px,calc(100vh-260px))] min-h-0 min-w-0 flex-col overflow-hidden" style={{ "--main-inset": "0px" }}>
            <ScrollRoot.Provider value={null}>
                <DirectoryBrowserTab section={section} onSectionChange={onSectionChange} />
            </ScrollRoot.Provider>
        </div>
    );
}

export default function SectionBody({
    address, sections = null, entitlements = null, onOpen = () => {}, onScopeNavigate = null,
    theme = "dark", onToggleTheme = null, onLogout = null,
}) {
    const [group, section] = address.split("/");
    if (group === "services") return <DataSourcesPage source={section} onOpenDirectory={() => onOpen("directory/directories")} />;
    switch (address) {
        case "endpoints/install":
            return <ConnectPage checkIns={sections?.has("enrolled") ? <EnrolledEndpoints onScopeNavigate={onScopeNavigate} /> : null} />;
        case "endpoints/enrolled": return <EnrolledEndpoints onScopeNavigate={onScopeNavigate} />;
        case "endpoints/tokens": return <ProvisioningTokensPage onCreate={() => onOpen("endpoints/install")} />;
        case "endpoints/installers": return <InstallersPage />;
        case "endpoints/updates": return <ArtifactRolloutsPage entitlements={entitlements} />;
        case "endpoints/every-endpoint": return <EndpointConfigurationSection />;
        case "endpoints/browser-chats": return <BrowserCaptureSettings />;
        case "endpoints/collectors": return <OtelMirrorSettings />;
        case "directory/directories": return <IntegrationsTab />;
        case "directory/users":
        case "directory/groups":
        case "directory/devices":
            return <Browse section={section} onSectionChange={(next) => onOpen(`directory/${next}`)} />;
        case "directory/no-person": return <UnassignedEndpointsPage entitlements={entitlements} />;
        case "access/people": return <UserManagementPage tab="users" onOpenVisibility={() => onOpen("access/visibility")} />;
        case "access/visibility": return <UserManagementPage tab="visibility" />;
        case "access/requests": return <EscalationsPage />;
        case "notifications/destinations": return <NotificationsPage />;
        case "trace-review/standards": return <ReviewStandards />;
        case "trace-review/policy": return <ReviewPolicy />;
        case "trace-review/norms": return <ReviewProfileItems kind="norms" />;
        case "trace-review/intents": return <ReviewProfileItems kind="intents" />;
        case "feedback/report": return <ReportPage />;
        case "account/sign-in":
        case "account/theme":
            return <AccountProfile theme={theme} onToggleTheme={onToggleTheme} onLogout={onLogout} />;
        case "account/inbox": return <AccountNotifications />;
        default: return null;
    }
}
